<template>
  <div class="upload-component">
    <!-- File Selection Area (Top Section) -->
    <fieldset class="file-selection-area mb-4">
      <legend class="font-weight-bold">Đính kèm</legend>

      <div
        class="drop-zone"
        :class="{ 'drop-zone-active': isDragging }"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="drop-zone-content text-center">
          <div class="upload-icon-container">
            <i class="fas fa-cloud-upload-alt"></i>
          </div>
          <div>
            <p class="mb-3">Kéo thả file vào đây để tải lên hoặc</p>
            <div class="d-flex justify-content-center">
              <b-button variant="danger" class="mr-3" @click="openFileSelector">
                <i class="fas fa-desktop mr-2"></i>
                CHỌN TỪ MÁY
              </b-button>
            </div>
          </div>
          <input
            ref="fileInput"
            type="file"
            multiple
            class="d-none"
            accept="image/*"
            @change="onFileSelected"
          />
        </div>
      </div>
    </fieldset>

    <!-- File List Display Area (Bottom Section) -->
    <div class="file-list-area" v-if="loading">
      <div class="text-center py-5">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <p class="mt-2">Đang tải dữ liệu...</p>
      </div>
    </div>

    <div class="file-list-area" v-else-if="allFiles.length === 0">
      <div class="text-center py-5 text-muted">
        <i class="fas fa-images fa-3x mb-3"></i>
        <p>Chưa có hình ảnh nào được tải lên</p>
      </div>
    </div>

    <div class="file-list-area" v-else>
      <b-row>
        <b-col
          v-for="file in allFiles"
          :key="file.id"
          cols="12"
          sm="6"
          md="6"
          lg="6"
          class="mb-4"
        >
          <div
            class="file-item"
            :class="{
              'file-item-success': file.status === 'READY',
              'file-item-pending':
                file.status === 'PENDING' || file.status === 'PROCESSING',
              'file-item-error': file.status === 'FAILED',
              'file-item-uploading': file.uploading,
            }"
          >
            <!-- Thumbnail -->
            <div class="file-thumbnail">
              <div
                v-if="file.url && isImageFile(file.originalName)"
                class="thumbnail-wrapper"
                v-b-tooltip.hover
                title="Click để phóng to"
                @click="showImagePreview(file)"
              >
                <img
                  :src="file.url"
                  :alt="file.originalName"
                  class="img-fluid cursor-pointer"
                />
                <div class="zoom-overlay">
                  <i class="fas fa-search-plus"></i>
                </div>
              </div>
              <div v-else class="file-icon">
                <i class="fas fa-file-image"></i>
              </div>
            </div>

            <!-- File Info -->
            <div class="file-info">
              <div
                class="file-name"
                v-b-tooltip.hover
                :title="file.originalName"
              >
                {{ truncateFileName(file.originalName) }}
              </div>
              <div class="file-meta">
                <span v-if="file.size">{{ formatFileSize(file.size) }}</span>
                <span v-if="file.createdAt">{{
                  formatDate(file.createdAt)
                }}</span>
              </div>

              <!-- Progress Bar for uploading files -->
              <b-progress
                v-if="file.uploading"
                :value="file.progress"
                max="100"
                height="4px"
                class="mt-2"
              ></b-progress>

              <!-- Status Messages -->
              <div
                v-if="file.status === 'FAILED'"
                class="file-status text-danger"
              >
                <i class="fas fa-exclamation-circle mr-1"></i>
                Lỗi tải lên
              </div>
              <div
                v-else-if="file.status === 'READY'"
                class="file-status text-success"
              >
                <i class="fas fa-check-circle mr-1"></i>
                Đã tải lên
              </div>
              <div v-else-if="file.uploading" class="file-status text-primary">
                <i class="fas fa-spinner fa-spin mr-1"></i>
                Đang tải lên {{ file.progress }}%
              </div>
              <div
                v-else-if="
                  file.status === 'PENDING'
                "
                class="file-status text-warning"
              >
                <i class="fas fa-clock mr-1"></i>
                Đang xử lý
              </div>
              <div
                v-else-if="file.status === 'PROCESSING'
                "
                class="file-status text-warning"
              >
                <i class="fas fa-eye mr-1"></i>
                Click để xem chi tiết
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="file-actions">
              <b-button
                variant="link"
                size="sm"
                class="p-0 text-danger"
                @click="confirmDelete(file)"
                :disabled="file.uploading"
              >
                <i class="fas fa-times"></i>
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Confirmation Modal -->
    <b-modal
      ref="deleteModal"
      title="Xác nhận xóa"
      ok-title="Xóa"
      cancel-title="Hủy"
      ok-variant="danger"
      @ok="deleteFile"
    >
      <p>Bạn có chắc chắn muốn xóa hình ảnh này?</p>
    </b-modal>

    <!-- Image Preview Lightbox -->
    <vue-easy-lightbox
      escDisabled
      :visible="lightboxVisible"
      :imgs="lightboxImages"
      :index="lightboxIndex"
      @hide="handleHideLightbox"
    />
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { mapActions } from 'vuex';
import VueEasyLightbox from 'vue-easy-lightbox';

export default {
  name: 'Upload',
  props: {
    modelId: {
      type: [String, Number],
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
    maxFiles: {
      type: Number,
      default: 10,
    },
    maxFileSize: {
      type: Number,
      default: 5 * 1024 * 1024, // 5MB
    },
    acceptedFileTypes: {
      type: Array,
      default: () => [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/svg+xml',
        'image/bmp',
        'image/tiff',
      ],
    },
  },
  data() {
    return {
      isDragging: false,
      loading: false,
      uploadedFiles: [],
      existingFiles: [],
      fileToDelete: null,
      lightboxVisible: false,
      lightboxImages: [],
      lightboxIndex: 0,
    };
  },
  computed: {
    allFiles() {
      // Combine uploaded files and existing files
      return [...this.uploadedFiles, ...this.existingFiles];
    },
  },
  mounted() {
    this.fetchExistingImages();
  },
  methods: {
    ...mapActions('images', ['deleteImage']),

    // File Selection Methods
    openFileSelector() {
      this.$refs.fileInput.click();
    },

    onDragOver() {
      this.isDragging = true;
    },

    onDragLeave() {
      this.isDragging = false;
    },

    onDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length) {
        this.processFiles(files);
      }
    },

    onFileSelected(event) {
      const files = event.target.files;
      if (files.length) {
        this.processFiles(files);
      }
    },

    // File Processing and Validation
    processFiles(fileList) {
      const files = Array.from(fileList);

      // Check if adding these files would exceed the maximum
      if (this.allFiles.length + files.length > this.maxFiles) {
        return makeToastFaile(`Không thể tải lên quá ${this.maxFiles} tệp`);
      }

      // Validate and prepare files for upload
      const validFiles = files.filter((file) => {
        // Check file type
        if (!this.acceptedFileTypes.includes(file.type)) {
          makeToastFaile(`Loại tệp không hỗ trợ: ${file.name}`);
          return false;
        }

        // Check file size
        if (file.size > this.maxFileSize) {
          makeToastFaile(
            `Tệp quá lớn: ${
              file.name
            }. Kích thước tối đa là ${this.formatFileSize(this.maxFileSize)}`,
          );
          return false;
        }

        return true;
      });

      if (validFiles.length > 0) {
        this.uploadFiles(validFiles);
      }
    },

    // API Calls
    fetchExistingImages() {
      this.loading = true;

      ApiService.get(
        `/v1/images/by-module/all?modelName=${this.modelName}&modelId=${this.modelId}`,
      )
        .then((response) => {
          if (response.data.success) {
            this.existingFiles = response.data.data?.images || [];
          } else {
            makeToastFaile('Không thể tải danh sách hình ảnh');
          }
        })
        .catch((error) => {
          console.error('Error fetching images:', error);
          makeToastFaile('Không thể tải danh sách hình ảnh');
        })
        .finally(() => {
          this.loading = false;
        });
    },

    uploadFiles(files) {
      const formData = new FormData();

      // Add each file to the form data
      files.forEach((file) => {
        formData.append('files', file);

        // Create a temporary file object to show in the UI
        const tempFile = {
          id: `temp-${Date.now()}-${file.name}`,
          originalName: file.name,
          size: file.size,
          uploading: true,
          progress: 0,
          url: URL.createObjectURL(file),
          file: file,
        };

        this.uploadedFiles.push(tempFile);
      });

      // Upload the files with query parameters for model information
      const url = `/v1/images/uploads?modelId=${this.modelId}&modelName=${this.modelName}`;

      ApiService.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );

          // Update progress for all uploading files
          this.uploadedFiles.forEach((file) => {
            if (file.uploading) {
              file.progress = percentCompleted;
            }
          });
        },
      })
        .then((response) => {
          const { success, images, totalUploaded } = response.data;

          if (success && images) {
            // Remove temporary files
            this.uploadedFiles = this.uploadedFiles.filter(
              (file) => !file.uploading,
            );

            // Add the uploaded files to the list
            this.existingFiles = [...images, ...this.existingFiles];

            makeToastSuccess(
              `Đã tải lên ${totalUploaded ||
                images.length} hình ảnh thành công`,
            );

            // Emit upload complete event
            this.$emit('upload-complete', totalUploaded || images.length);
          }
        })
        .catch((error) => {
          console.error('Error uploading files:', error);
          makeToastFaile('Lỗi khi tải lên hình ảnh');

          // Mark uploading files as failed
          this.uploadedFiles.forEach((file) => {
            if (file.uploading) {
              file.uploading = false;
              file.status = 'FAILED';
            }
          });
        });
    },

    confirmDelete(file) {
      this.fileToDelete = file;
      this.$refs.deleteModal.show();
    },

    deleteFile() {
      if (!this.fileToDelete) return;

      const file = this.fileToDelete;

      // If it's a temporary file (still uploading), just remove it from the list
      if (file.id.startsWith('temp-')) {
        this.uploadedFiles = this.uploadedFiles.filter((f) => f.id !== file.id);
        return;
      }

      // Otherwise, call the API to delete the file
      // just un-use the asset, remove the image directly will trigger by even sourcing (will implement)
      ApiService.post(`v1/asset-usages/assets/${file.id}/unuse`, {
        modelName: this.modelName,
        modelId: this.modelId,
      })
        .then((response) => {
          if (response.data.success) {
            this.existingFiles = this.existingFiles.filter(
              (f) => f.id !== file.id,
            );
            makeToastSuccess('Đã xóa hình ảnh thành công');
          }
        })
        .catch((error) => {
          console.error('Error deleting file:', error);
          makeToastFaile('Lỗi khi xóa hình ảnh');
        });
    },

    // Utility Methods
    isImageFile(filename) {
      const ext = filename
        .split('.')
        .pop()
        .toLowerCase();
      return [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'webp',
        'svg',
        'bmp',
        'tiff',
      ].includes(ext);
    },

    truncateFileName(filename) {
      if (!filename) return '';

      if (filename.length > 20) {
        return filename.substring(0, 17) + '...';
      }
      return filename;
    },

    formatFileSize(bytes) {
      if (bytes < 1024) {
        return bytes + ' B';
      } else if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(2) + ' KB';
      } else {
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN');
    },

    // Lightbox Methods
    showImagePreview(file) {
      if (!file.url || !this.isImageFile(file.originalName)) {
        return;
      }

      // Get all image files
      const imageFiles = this.allFiles.filter((f) =>
        this.isImageFile(f.originalName),
      );

      // Find the index of the clicked file
      const index = imageFiles.findIndex((f) => f.id === file.id);

      // Set lightbox images and index
      this.lightboxImages = imageFiles.map((f) => f.url);
      this.lightboxIndex = index >= 0 ? index : 0;
      this.lightboxVisible = true;
    },

    handleHideLightbox() {
      this.lightboxVisible = false;
      this.lightboxImages = [];
      this.lightboxIndex = 0;
    },
  },
  components: {
    VueEasyLightbox,
  },
};
</script>

<style scoped>
.upload-component {
  margin-bottom: 2rem;
}

.file-selection-area {
  border: 1px solid #e4e6ef;
  border-radius: 0.42rem;
  padding: 1.5rem;
}

.file-selection-area legend {
  width: auto;
  padding: 0 0.5rem;
  margin-bottom: 0;
  font-size: 1.1rem;
  color: #3f4254;
}

.drop-zone {
  border: 2px dashed #e4e6ef;
  border-radius: 0.42rem;
  padding: 2rem;
  transition: all 0.3s ease;
}

.drop-zone-active {
  border-color: #f64e60;
  background-color: rgba(246, 78, 96, 0.05);
}

.drop-zone-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
}

.upload-icon-container {
  width: 67px;
  height: 67px;
  border-radius: 50%;
  border: 1px solid #f64e60;
  color: #f64e60;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-icon-container i {
  font-size: 24px;
  color: #f64e60;
}

.file-item {
  display: flex;
  border: 1px solid #e4e6ef;
  border-radius: 0.42rem;
  padding: 1rem;
  position: relative;
  height: 100%;
}

.file-item-success {
  border-color: #1bc5bd;
}

.file-item-pending {
  border-color: #ffa800;
}

.file-item-error {
  border-color: #f64e60;
}

.file-thumbnail {
  width: 60px;
  height: 60px;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.thumbnail-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  border-radius: 0.42rem;
  overflow: hidden;
}

.thumbnail-wrapper:hover .zoom-overlay {
  opacity: 1;
}

.thumbnail-wrapper:hover img {
  opacity: 0.7;
  transform: scale(1.05);
}

.zoom-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.zoom-overlay i {
  color: white;
  font-size: 18px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.file-thumbnail img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.file-icon {
  width: 60px;
  height: 60px;
  background-color: #f3f6f9;
  border-radius: 0.42rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-icon i {
  font-size: 24px;
  color: #7e8299;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 500;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-meta {
  font-size: 0.8rem;
  color: #b5b5c3;
  display: flex;
  gap: 0.5rem;
}

.file-status {
  font-size: 0.8rem;
  margin-top: 0.5rem;
}

.file-actions {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}
</style>
